﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { ProtectedRoute } from "~base/Pages/Shared/ProtectedRoute";
import { LoginPage } from "~base/Pages/Account/Login";
import { ExternalLoginPage } from "~base/Pages/Account/ExternalLogin";
import { LogoutPage } from "~base/Pages/Account/Logout";
import { ExternalApiCodePage } from "~base/Pages/Account/ExternalApiCode";
import { AccessDeniedPage } from "~base/Pages/Account/AccessDenied";
import { NotFoundPage } from "~base/Pages/Home/NotFound";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import LoginPageCustomization from "~irad/Pages/Account/Login";
import RegisterPageCustomization from "~irad/Pages/Account/Register";
import StartPageCustomization from "~irad/Pages/Home/Start";
import { StartPage } from "~base/Pages/Home/Start";
import { RegisterPage } from "~base/Pages/Account/Register";
import TinkPage from "./Pages/Tink";
import { ExternalLoginByCookieCallbackPage } from "~base/Pages/Account/ExternalLoginByCookieCallback";
import { SessionInvitePage } from "./Pages/Home/SessionInvite";
import type { PageRouteInfo } from "~base/Pages/Shared/PageRoute";
import { createPageRoute } from "~base/Pages/Shared/PageRoute";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type MainRouterProps = {
    children?: React.ReactNode;
};

//-------------------------------------------------------------------------------------
// Routes
//-------------------------------------------------------------------------------------

const account: PageRouteInfo[] = [
    {
        path: "Account/Login/*",
        component: <LoginPage customizations={LoginPageCustomization} />,
    },
    {
        path: "Account/ExternalLoginCallback/*",
        component: <ExternalLoginPage />,
    },
    { path: "Account/Logout/*", component: <LogoutPage /> },
    {
        path: "Account/Register/*",
        component: (
            <ProtectedRoute>
                <RegisterPage customizations={RegisterPageCustomization} />
            </ProtectedRoute>
        ),
    },
    { path: "Account/ExternalApiCode", component: <ExternalApiCodePage /> },
    { path: "Account/AccessDenied/*", component: <AccessDeniedPage /> },
    { path: `Account/Forbidden/*`, component: <AccessDeniedPage /> },
    {
        path: "Account/ExternalLoginByCookieCallback/*",
        component: <ExternalLoginByCookieCallbackPage />,
    },
];

const home: PageRouteInfo[] = [
    {
        path: "Home/Start/*",
        component: (
            <ProtectedRoute>
                <StartPage customizations={StartPageCustomization} />
            </ProtectedRoute>
        ),
    },
    {
        path: "Home/SessionInviteAdvisor/*",
        component: (
            <ProtectedRoute>
                <SessionInvitePage />
            </ProtectedRoute>
        ),
    },
    {
        path: "Home/SessionInvite/*",
        component: (
            <ProtectedRoute>
                <SessionInvitePage />
            </ProtectedRoute>
        ),
    },
    {
        path: "/",
        component: (
            <ProtectedRoute>
                <StartPage customizations={StartPageCustomization} />
            </ProtectedRoute>
        ),
    },
    { path: "/*", component: <NotFoundPage /> },
];

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const MainRouter: React.FunctionComponent<MainRouterProps> = (
    props: MainRouterProps
) => {
    const customer: PageRouteInfo[] = [
        { path: "DataGathering/Tink", component: <TinkPage /> },
    ];

    return (
        <BrowserRouter>
            <Routes>
                {account.map((x) => createPageRoute(x))}
                {customer.map((x) => createPageRoute(x))}
                {home.map((x) => createPageRoute(x))}
            </Routes>
        </BrowserRouter>
    );
};

//-------------------------------------------------------------------------------------
// Exports
//-------------------------------------------------------------------------------------

export default MainRouter;
